/* ============================================
   BRAIN BEAR - Scroll-Triggered Floating Gummies
   Decorative 3D gummy bears that float across
   the screen as the user scrolls past sections.
   ============================================ */

import * as THREE from 'three';
import { OBJLoader } from 'three/addons/loaders/OBJLoader';

class FloatingGummies {
  constructor() {
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.canvas = document.getElementById('floating-gummies-canvas');
    if (!this.canvas) return;

    // Configuration
    this.MAX_BEARS = this.isMobile ? 3 : 5;
    this.lastSpawnTime = 0;
    this.SPAWN_COOLDOWN = 3000; // 3 seconds between spawns
    this.FRUSTUM_SIZE = 20;
    this.BASE_MODEL_SCALE = 1.5 / 6.5;
    this.FLOAT_SCALE_MIN = 0.3;
    this.FLOAT_SCALE_MAX = 0.5;

    this.activeBears = [];
    this.triggeredSections = new Set();
    this.bearGeometry = null;
    this.ready = false;
    this.isPageVisible = true;
    this.isMobile = window.innerWidth < 768;
    this.clock = new THREE.Clock();

    this.objUrl = this.canvas.dataset.objUrl;

    // Mouse interaction (desktop only)
    this.mouseWorld = new THREE.Vector2(0, 0);
    this.mouseActive = false;

    this.colorVariations = [
      0xCC4E00, 0xB34200, 0xD45500, 0xA03800, 0xC04800
    ];

    this.isScrolling = false;
    this.scrollTimeout = null;

    this.init();
    this.loadModel();
    this.setupSectionObservers();
    this.setupListeners();
    if (!this.isMobile) this.setupMouseTracking();
    this.animate();
  }

  init() {
    // Scene
    this.scene = new THREE.Scene();

    // Orthographic camera — world coords map linearly to screen edges
    const aspect = window.innerWidth / window.innerHeight;
    this.camera = new THREE.OrthographicCamera(
      -this.FRUSTUM_SIZE * aspect / 2,
       this.FRUSTUM_SIZE * aspect / 2,
       this.FRUSTUM_SIZE / 2,
      -this.FRUSTUM_SIZE / 2,
      0.1, 100
    );
    this.camera.position.z = 30;
    this.updateFrustumDimensions();

    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'low-power'
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    // Lighting (simplified)
    this.scene.add(new THREE.AmbientLight(0xFFF5E6, 0.8));

    const keyLight = new THREE.DirectionalLight(0xFFFFFF, 1.0);
    keyLight.position.set(5, 8, 10);
    this.scene.add(keyLight);

    const fillLight = new THREE.PointLight(0xF5C542, 0.4, 50);
    fillLight.position.set(-6, 2, 8);
    this.scene.add(fillLight);
  }

  updateFrustumDimensions() {
    const aspect = window.innerWidth / window.innerHeight;
    this.frustumHalfWidth = this.FRUSTUM_SIZE * aspect / 2;
    this.frustumHalfHeight = this.FRUSTUM_SIZE / 2;
  }

  loadModel() {
    const loader = new OBJLoader();
    loader.load(this.objUrl, (obj) => {
      let mergedGeometry = null;
      obj.traverse((child) => {
        if (child.isMesh) {
          const geom = child.geometry.clone();
          geom.applyMatrix4(child.matrixWorld);
          if (!mergedGeometry) mergedGeometry = geom;
        }
      });
      if (!mergedGeometry) return;
      mergedGeometry.computeBoundingBox();
      mergedGeometry.center();
      this.bearGeometry = mergedGeometry;
      this.ready = true;
    });
  }

  createBearMaterial() {
    const color = new THREE.Color(
      this.colorVariations[Math.floor(Math.random() * this.colorVariations.length)]
    );
    return new THREE.MeshStandardMaterial({
      color: color,
      roughness: 0.22,
      metalness: 0.05,
      transparent: true,
      opacity: 0,
      side: THREE.FrontSide,
      envMapIntensity: 0.8
    });
  }

  setupSectionObservers() {
    const sectionSelectors = [
      '#product', '#benefits', '#ingredients', '#reviews', '.cta-section'
    ];

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.id || entry.target.className;
          if (!this.triggeredSections.has(id)) {
            this.triggeredSections.add(id);
            this.spawnBatch();
          }
        }
      });
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -10% 0px'
    });

    sectionSelectors.forEach((sel) => {
      const el = document.querySelector(sel);
      if (el) observer.observe(el);
    });
  }

  setupListeners() {
    window.addEventListener('resize', () => this.onResize());

    document.addEventListener('visibilitychange', () => {
      this.isPageVisible = !document.hidden;
    });

    // Pause rendering during active scroll for smoother page performance
    window.addEventListener('scroll', () => {
      this.isScrolling = true;
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
      }, 150);
    }, { passive: true });
  }

  setupMouseTracking() {
    document.addEventListener('mousemove', (e) => {
      this.mouseActive = true;
      // Convert pixel coords to NDC (-1 to +1), then to world coords
      const ndcX = (e.clientX / window.innerWidth) * 2 - 1;
      const ndcY = -(e.clientY / window.innerHeight) * 2 + 1;
      const aspect = window.innerWidth / window.innerHeight;
      const halfH = this.FRUSTUM_SIZE / 2;
      const halfW = halfH * aspect;
      this.mouseWorld.set(ndcX * halfW, ndcY * halfH);
    });

    document.addEventListener('mouseleave', () => {
      this.mouseActive = false;
    });
  }

  onResize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const aspect = w / h;

    this.camera.left = -this.FRUSTUM_SIZE * aspect / 2;
    this.camera.right = this.FRUSTUM_SIZE * aspect / 2;
    this.camera.top = this.FRUSTUM_SIZE / 2;
    this.camera.bottom = -this.FRUSTUM_SIZE / 2;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(w, h);
    this.updateFrustumDimensions();
    this.isMobile = w < 768;
  }

  spawnBatch() {
    if (!this.ready) return;
    const now = Date.now();
    if (now - this.lastSpawnTime < this.SPAWN_COOLDOWN) return;
    this.lastSpawnTime = now;

    const count = this.isMobile ? 1 : 2;
    for (let i = 0; i < count; i++) {
      setTimeout(() => this.spawnBear(), i * 400);
    }
  }

  spawnBear() {
    if (!this.ready || this.activeBears.length >= this.MAX_BEARS) return;

    const mesh = new THREE.Mesh(this.bearGeometry, this.createBearMaterial());

    // Scale — small but noticeable
    const scaleVar = this.FLOAT_SCALE_MIN + Math.random() * (this.FLOAT_SCALE_MAX - this.FLOAT_SCALE_MIN);
    const s = this.BASE_MODEL_SCALE * scaleVar;
    mesh.scale.set(s, s, s);

    // Pick spawn edge and compute trajectory
    const edge = this.pickSpawnEdge();
    const { startPos, velocity } = this.computeTrajectory(edge);

    mesh.position.copy(startPos);
    mesh.rotation.set(
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2
    );

    const bearData = {
      mesh,
      velocity,
      rotSpeed: new THREE.Vector3(
        (Math.random() - 0.5) * 1.5,
        (Math.random() - 0.5) * 1.5,
        (Math.random() - 0.5) * 0.8
      ),
      age: 0,
      maxAge: 8 + Math.random() * 4,
      fadeInDuration: 0.5
    };

    this.scene.add(mesh);
    this.activeBears.push(bearData);
  }

  pickSpawnEdge() {
    const edges = ['left', 'right', 'bottom'];
    return edges[Math.floor(Math.random() * edges.length)];
  }

  computeTrajectory(edge) {
    const halfW = this.frustumHalfWidth;
    const halfH = this.frustumHalfHeight;
    const margin = 2;
    const speed = 1.0 + Math.random() * 1.5;

    let startPos, velocity;

    switch (edge) {
      case 'left':
        startPos = new THREE.Vector3(
          -halfW - margin,
          (Math.random() - 0.5) * halfH * 1.5,
          (Math.random() - 0.5) * 4
        );
        velocity = new THREE.Vector3(
          speed,
          (Math.random() - 0.3) * speed * 0.4,
          0
        );
        break;

      case 'right':
        startPos = new THREE.Vector3(
          halfW + margin,
          (Math.random() - 0.5) * halfH * 1.5,
          (Math.random() - 0.5) * 4
        );
        velocity = new THREE.Vector3(
          -speed,
          (Math.random() - 0.3) * speed * 0.4,
          0
        );
        break;

      case 'bottom':
        startPos = new THREE.Vector3(
          (Math.random() - 0.5) * halfW * 1.5,
          -halfH - margin,
          (Math.random() - 0.5) * 4
        );
        velocity = new THREE.Vector3(
          (Math.random() - 0.5) * speed * 0.5,
          speed,
          0
        );
        break;
    }

    return { startPos, velocity };
  }

  updateBear(bear, dt) {
    bear.age += dt;

    // Movement
    bear.mesh.position.x += bear.velocity.x * dt;
    bear.mesh.position.y += bear.velocity.y * dt;

    // Gentle sinusoidal wobble perpendicular to travel
    const wobble = Math.sin(bear.age * 2.0) * 0.3 * dt;
    if (Math.abs(bear.velocity.y) > Math.abs(bear.velocity.x)) {
      bear.mesh.position.x += wobble;
    } else {
      bear.mesh.position.y += wobble;
    }

    // Rotation
    bear.mesh.rotation.x += bear.rotSpeed.x * dt;
    bear.mesh.rotation.y += bear.rotSpeed.y * dt;
    bear.mesh.rotation.z += bear.rotSpeed.z * dt;

    // Mouse bounce interaction — smack them hard
    if (this.mouseActive) {
      const dx = bear.mesh.position.x - this.mouseWorld.x;
      const dy = bear.mesh.position.y - this.mouseWorld.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const BOUNCE_RADIUS = 3.5;
      const BOUNCE_STRENGTH = 35.0;

      if (dist < BOUNCE_RADIUS && dist > 0.01) {
        const nx = dx / dist;
        const ny = dy / dist;
        // Aggressive inverse-square: way stronger up close
        const ratio = 1 - dist / BOUNCE_RADIUS;
        const force = BOUNCE_STRENGTH * ratio * ratio;
        bear.velocity.x += nx * force * dt;
        bear.velocity.y += ny * force * dt;
        // Violent tumble spin on impact
        bear.rotSpeed.x += (Math.random() - 0.5) * 8;
        bear.rotSpeed.y += (Math.random() - 0.5) * 8;
        bear.rotSpeed.z += (Math.random() - 0.5) * 6;
        bear.bounced = true;
      }
    }

    // Light drag so they eventually settle after being launched
    if (bear.bounced) {
      const DRAG = 0.99;
      bear.velocity.x *= DRAG;
      bear.velocity.y *= DRAG;
    }

    // Fade in
    if (bear.age < bear.fadeInDuration) {
      bear.mesh.material.opacity = (bear.age / bear.fadeInDuration) * 0.93;
    } else {
      bear.mesh.material.opacity = 0.93;
    }
  }

  shouldRemoveBear(bear) {
    const pos = bear.mesh.position;
    const margin = 3;
    return (
      bear.age > bear.maxAge ||
      pos.x > this.frustumHalfWidth + margin ||
      pos.x < -this.frustumHalfWidth - margin ||
      pos.y > this.frustumHalfHeight + margin ||
      pos.y < -this.frustumHalfHeight - margin
    );
  }

  cleanupBears() {
    for (let i = this.activeBears.length - 1; i >= 0; i--) {
      if (this.shouldRemoveBear(this.activeBears[i])) {
        const bear = this.activeBears[i];
        this.scene.remove(bear.mesh);
        bear.mesh.material.dispose();
        this.activeBears.splice(i, 1);
      }
    }
  }

  animate() {
    requestAnimationFrame(() => this.animate());

    if (!this.isPageVisible || this.activeBears.length === 0) return;

    const dt = Math.min(this.clock.getDelta(), 0.1);

    this.activeBears.forEach((bear) => this.updateBear(bear, dt));
    this.cleanupBears();

    if (this.activeBears.length > 0) {
      this.renderer.render(this.scene, this.camera);
    } else {
      this.renderer.clear();
    }
  }
}

// Initialize when DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new FloatingGummies());
} else {
  new FloatingGummies();
}
